import { Inject, Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap, catchError, tap, take, delay } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

import * as projectsActions from '../actions/projects.action';
import * as fromRoot from '@usimweb/state/router-store';

import { AuthHandler } from '@usimweb/core-auth';
import { ApiService } from '@usimweb/core/services';
import { Env, Projects, Role, Sharing, ToggleSharing } from '@usimweb/core/models';

// import { CacheService } from '../../../core/services/domain/cache/cache.service';
// import { MouseHighlighterService } from '../../../core/services/application/mouse/highlighter.service';

@Injectable()
export class ProjectEffects {
  private readonly DASHBOARD_EXPIRE_TIME = 1440 * 1;

  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    @Inject('environment') private environment: Env,
    private snackBar: MatSnackBar // private cacheService: CacheService, // private mouseHighlighter: MouseHighlighterService
  ) {}

  loadProjects$ = createEffect(() => {
    return this.actions$.pipe(
      tap(() => {}),
      ofType(projectsActions.LOAD_PROJECTS),
      switchMap(() => {
        // const cached = this.cacheService.isCached(projectsActions.LOAD_PROJECTS);
        // if (cached) {
        //   const projects = this.cacheService.load(projectsActions.LOAD_PROJECTS);
        //   console.log('cached');
        //   console.log(projects);
        //   return of(new projectsActions.LoadProjectsSuccess(projects));
        // }
        return this.apiService.getProjects().pipe(
          map((projects) => {
            // this.cacheService.save({
            //   key: projectsActions.LOAD_PROJECTS,
            //   data: projects,
            //   expireTime: this.DASHBOARD_EXPIRE_TIME,
            // });

            const currentUser = AuthHandler.basfUserName.toLocaleLowerCase();

            projects.forEach((project: Projects) => {
              if (project.uuid === undefined && project['id']) {
                project.uuid = project['id'];
              }
              project.isOwner = currentUser === project.created_by.toLocaleLowerCase() ? true : false;

              const userRoles: any = {};
              Object.entries(project.user_roles).forEach(([key, value]) => {
                userRoles[key.toLocaleLowerCase()] = value;
              });

              if (userRoles[currentUser] === 'reviewer') {
                project.hasPermission = true;
              } else if (userRoles[currentUser] === 'owner') {
                project.hasPermission = true;
              } else {
                project.hasPermission = false;
              }
            });

            return new projectsActions.LoadProjectsSuccess(projects);
          }),
          catchError((error) => of(new projectsActions.LoadProjectsFail(error)))
        );
      })
    );
  });

  loadProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.LOAD_PROJECT),
      map((action: projectsActions.LoadProject) => action.payload),
      switchMap((uuid: string) => {
        return this.apiService.getProjectById(uuid).pipe(
          map((project) => {
            return new projectsActions.LoadProjectSuccess(project);
          }),
          catchError((error) => of(new projectsActions.LoadProjectFail(error)))
        );
      })
    );
  });

  deleteProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.DELETE_PROJECT),
      map((action: projectsActions.DeleteProject) => action.payload),
      switchMap((projects: Projects) => {
        return this.apiService.deleteProject(projects.uuid).pipe(
          map(() => {
            this.snackBar.open('Deleted project: ' + projects.name, 'OK', { duration: 2000 });
            return new projectsActions.DeleteProjectSuccess(projects);
          }),
          catchError((error) => of(new projectsActions.DeleteProjectFails(error)))
        );
      })
    );
  });

  favProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.FAV_PROJECT),
      map((action: projectsActions.FavProject) => action.payload),
      switchMap((projects: Projects) => {
        return this.apiService.updateFavouriteStatus(projects.uuid, !projects.is_favorite).pipe(
          map(() => {
            this.snackBar.open(
              projects.is_favorite === true
                ? 'Unset ' + projects.name + ' as favorite'
                : 'Set ' + projects.name + ' as favorite',
              'OK',
              { duration: 2000 }
            );
            return new projectsActions.FavProjectSuccess(projects);
          }),
          catchError((error) => of(new projectsActions.FavProjectSuccess(error)))
        );
      })
    );
  });

  createProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.CREATE_PROJECTS),
      map((action: projectsActions.CreateProjects) => action.payload),
      switchMap((project: Projects) => {
        if (!project.name && !project) {
          this.snackBar.open('Please add a project name...', 'OK', { duration: 5000 });
          return of(new projectsActions.CreateProjectsFail('Please add a project name...'));
        }
        return this.apiService.addProject(project).pipe(
          map((newProject) => {
            return new projectsActions.CreateProjectsSuccess(newProject);
          }),
          catchError((error) => of(new projectsActions.CreateProjectsFail(error)))
        );
      })
    );
  });

  requestSupport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.REQUEST_SUPPORT),
      map((action: projectsActions.RequestSupport) => action.payload),
      switchMap((obj: { uuid: string; reqSupport: {} }) => {
        return this.apiService.requestSupport(obj.uuid, obj.reqSupport).pipe(
          map((project: Projects) => {
            this.snackBar.open('Support request sent successfully', 'OK', { duration: 2000 });
            return new projectsActions.RequestSupportSuccess(project);
          }),
          catchError((error) => of(new projectsActions.RequestSupportFail(error)))
        );
      })
    );
  });

  unRequestSupport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.UNREQUEST_SUPPORT),
      map((action: projectsActions.UnrequestSupport) => action.payload),
      switchMap((obj: { uuid: string; request_id: string }) => {
        return this.apiService.requestUnSupport(obj).pipe(
          map((project: Projects) => {
            this.snackBar.open('Support unrequest sent successfully', 'OK', { duration: 2000 });
            return new projectsActions.UnrequestSupportSuccess(project);
          }),
          catchError((error) => of(new projectsActions.RequestSupportFail(error)))
        );
      })
    );
  });

  addUserToProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.ADD_USER_PROJECT),
      map((action: projectsActions.AddUserProjects) => action.payload),
      switchMap((toggleSharing: ToggleSharing) => {
        return this.apiService.addUserToProject(toggleSharing.uuid, toggleSharing.data).pipe(
          map((result) => {
            const userSharing: Sharing = {
              uuid: toggleSharing.uuid,
              data: toggleSharing.data,
              user_roles: result.user_roles,
            };

            return new projectsActions.AddUserProjectsSuccess(userSharing);
          }),
          catchError((error) => of(new projectsActions.AddUserProjectsFail(error)))
        );
      })
    );
  });

  removeUserFromProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.RM_USER_PROJECT),
      map((action: projectsActions.RmUserProjects) => action.payload),
      switchMap((toggleSharing: ToggleSharing) => {
        return this.apiService.removeUserFromProject(toggleSharing.uuid, toggleSharing.data).pipe(
          map((result) => {
            const userSharing: Sharing = {
              uuid: toggleSharing.uuid,
              data: toggleSharing.data,
              user_roles: result.user_roles,
            };
            return new projectsActions.RmUserProjectsSuccess(userSharing);
          }),
          catchError((error) => of(new projectsActions.RmUserProjectsFail(error)))
        );
      })
    );
  });

  changeUserRoleProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.CHANGE_USER_ROLE),
      map((action: projectsActions.ChangeUserProjects) => action.payload),
      switchMap((role: Role) => {
        return this.apiService.changeUserRole(role.uuid, role.data).pipe(
          map(() => {
            return new projectsActions.ChangeUserProjectsSuccess(role);
          }),
          catchError((error) => of(new projectsActions.ChangeUserProjectsFail(error)))
        );
      })
    );
  });

  // disclaimerProject$ = this.actions$
  //   .pipe(ofType(projectsActions.LOAD_PROJECTS))
  //   .pipe(map(() => this.disclaimer.project()));

  $goToSim = createEffect(() => {
    return this.actions$.pipe(
      ofType(projectsActions.CREATE_PROJECTS_SUCCESS),
      tap(() => {
        // this.mouseHighlighter.lastClick.subscribe((mouse) => {
        //   console.log('last click');
        //   console.log(mouse);
        //   this.mouseHighlighter.mouseObserver.next({ x: mouse.x, y: mouse.y });
        // });
        //
      }),
      delay(3000),
      map((action: projectsActions.CreateProjectsSuccess) => {
        return new fromRoot.Go({
          path: ['app/' + this.environment.newProjectLink + '/' + action.payload.uuid],
        });
      })
    );
  });
}
